import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, Search, MapPin, Clock, Users, Mic, Video, Heart,
  ChevronRight, X, Loader2, CheckCircle, AlertCircle, Star, Handshake,
  CalendarDays, Globe, Camera
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const EVENT_TYPES = [
  { id: 'appearance', label: 'Appearance', icon: Star, color: 'bg-yellow-500' },
  { id: 'speaking', label: 'Speaking', icon: Mic, color: 'bg-blue-500' },
  { id: 'interview', label: 'Interview', icon: Video, color: 'bg-purple-500' },
  { id: 'podcast', label: 'Podcast', icon: Mic, color: 'bg-pink-500' },
  { id: 'community', label: 'Community', icon: Heart, color: 'bg-red-500' },
  { id: 'activation', label: 'Activation', icon: Handshake, color: 'bg-orange-500' },
  { id: 'meet_greet', label: 'Meet & Greet', icon: Users, color: 'bg-cyan-500' },
  { id: 'charity', label: 'Charity', icon: Heart, color: 'bg-emerald-500' }
];

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-500' },
  confirmed: { label: 'Confirmed', color: 'bg-green-500' },
  completed: { label: 'Completed', color: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500' }
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'text-red-400' },
  medium: { label: 'Medium', color: 'text-yellow-400' },
  low: { label: 'Low', color: 'text-gray-400' }
};

const AEEvents = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('list'); // list, calendar

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchEvents();
    }
  }, [userEmail, filterStatus, filterType]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
      if (data.role === 'admin') {
        fetchStats();
      }
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      let url = `${API}/api/always-elevate/events?user_email=${encodeURIComponent(userEmail)}`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }
      if (filterType !== 'all') {
        url += `&event_type=${filterType}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/events/stats?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const filteredEvents = events.filter(e => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return e.title?.toLowerCase().includes(search) ||
             e.location?.toLowerCase().includes(search) ||
             e.venue?.toLowerCase().includes(search);
    }
    return true;
  });

  const getEventIcon = (eventType) => {
    const type = EVENT_TYPES.find(t => t.id === eventType);
    return type?.icon || Calendar;
  };

  const getEventColor = (eventType) => {
    const type = EVENT_TYPES.find(t => t.id === eventType);
    return type?.color || 'bg-gray-500';
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const isUpcoming = (date) => {
    return new Date(date) > new Date();
  };

  const getDaysUntil = (date) => {
    const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  // Group events by month for calendar view
  const groupedEvents = filteredEvents.reduce((acc, event) => {
    const month = new Date(event.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(event);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-events-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">
                {isAdmin ? 'ELLE MAXWELL AGENCY' : 'ALWAYS ELEVATE'}
              </p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-events-title">
                {isAdmin ? 'Events & Activations' : 'Upcoming Events'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {events.filter(e => isUpcoming(e.date)).length} upcoming events
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <>
                  <div className="flex items-center bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setView('list')}
                      className={`px-3 py-1.5 rounded text-sm ${view === 'list' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                    >
                      List
                    </button>
                    <button
                      onClick={() => setView('calendar')}
                      className={`px-3 py-1.5 rounded text-sm ${view === 'calendar' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                    >
                      Timeline
                    </button>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                    data-testid="create-event-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Event
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Row (Admin Only) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CalendarDays className="w-5 h-5 text-[#C4A052]" />
              </div>
              <p className="text-2xl font-bold">{stats.upcoming}</p>
              <p className="text-xs text-gray-400">Upcoming Events</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold">{formatNumber(stats.total_attendance)}</p>
              <p className="text-xs text-gray-400">Expected Attendance</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-2xl font-bold">
                {stats.by_type?.find(t => t._id === 'community')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">Community Events</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Handshake className="w-5 h-5 text-orange-400" />
              </div>
              <p className="text-2xl font-bold">
                {stats.by_type?.find(t => t._id === 'activation')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">Brand Activations</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
              data-testid="event-search"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filterStatus === 'all' ? 'bg-[#C4A052] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All
            </button>
            {Object.entries(STATUS_CONFIG).slice(0, 3).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterStatus === key ? `${config.color} text-white` : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-sm text-gray-400">Type:</span>
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              filterType === 'all' ? 'bg-white/20 text-white' : 'bg-white/5 text-gray-400'
            }`}
          >
            All
          </button>
          {EVENT_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setFilterType(filterType === type.id ? 'all' : type.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-colors ${
                  filterType === type.id ? `${type.color} text-white` : 'bg-white/5 text-gray-400'
                }`}
              >
                <Icon className="w-3 h-3" />
                {type.label}
              </button>
            );
          })}
        </div>

        {/* Timeline View */}
        {view === 'calendar' && isAdmin && (
          <div className="space-y-8" data-testid="events-timeline">
            {Object.entries(groupedEvents).map(([month, monthEvents]) => (
              <div key={month}>
                <h3 className="text-lg font-semibold text-[#C4A052] mb-4">{month}</h3>
                <div className="space-y-3">
                  {monthEvents.map(event => {
                    const EventIcon = getEventIcon(event.event_type);
                    return (
                      <div
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        className="flex items-start gap-4 bg-black border border-white/20 rounded-xl p-4 cursor-pointer transition-all"
                      >
                        <div className="text-center min-w-[60px]">
                          <p className="text-2xl font-bold">{new Date(event.date).getDate()}</p>
                          <p className="text-xs text-gray-400">{new Date(event.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                        </div>
                        <div className={`w-1 h-16 rounded-full ${getEventColor(event.event_type)}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{event.title}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[event.status]?.color || 'bg-gray-500'} text-white`}>
                              {STATUS_CONFIG[event.status]?.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </span>
                            )}
                            {event.expected_attendance && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {formatNumber(event.expected_attendance)}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {(view === 'list' || !isAdmin) && (
          <>
            {filteredEvents.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Events Found</h3>
                <p className="text-gray-500 text-sm">
                  {isAdmin ? 'Add your first event' : 'No upcoming events scheduled'}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="events-list">
                {filteredEvents.map(event => {
                  const EventIcon = getEventIcon(event.event_type);
                  const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.scheduled;
                  const upcoming = isUpcoming(event.date);
                  
                  return (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`bg-white/5 border rounded-xl p-5 cursor-pointer transition-all ${
                        !upcoming ? 'border-white/5 opacity-60' : 'border-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl ${getEventColor(event.event_type)} flex items-center justify-center flex-shrink-0`}>
                            <EventIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-lg">{event.title}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color} text-white`}>
                                {statusConfig.label}
                              </span>
                              {event.priority === 'high' && (
                                <span className="text-red-400 text-xs font-medium">HIGH PRIORITY</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(event.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.location}
                                </span>
                              )}
                              {event.expected_attendance && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {formatNumber(event.expected_attendance)} expected
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${upcoming ? 'text-[#C4A052]' : 'text-gray-500'}`}>
                            {getDaysUntil(event.date)}
                          </p>
                          <ChevronRight className="w-5 h-5 text-gray-500 mt-2 ml-auto" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${getEventColor(selectedEvent.event_type)} flex items-center justify-center`}>
                  {(() => {
                    const EventIcon = getEventIcon(selectedEvent.event_type);
                    return <EventIcon className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedEvent.title}</h3>
                  <p className="text-gray-400 text-sm capitalize">{selectedEvent.event_type?.replace('_', ' ')}</p>
                </div>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Priority */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_CONFIG[selectedEvent.status]?.color || 'bg-gray-500'} text-white`}>
                  {STATUS_CONFIG[selectedEvent.status]?.label || selectedEvent.status}
                </span>
                {selectedEvent.priority && (
                  <span className={`text-sm ${PRIORITY_CONFIG[selectedEvent.priority]?.color}`}>
                    {PRIORITY_CONFIG[selectedEvent.priority]?.label} Priority
                  </span>
                )}
                {selectedEvent.is_public === false && (
                  <span className="px-2 py-1 bg-white/10 rounded text-xs">Private Event</span>
                )}
              </div>

              {/* Date & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-1">Date & Time</h4>
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#C4A052]" />
                    {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                {selectedEvent.location && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Location</h4>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C4A052]" />
                      {selectedEvent.location}
                    </p>
                    {selectedEvent.venue && (
                      <p className="text-sm text-gray-400 ml-6">{selectedEvent.venue}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                  <p className="text-gray-300">{selectedEvent.description}</p>
                </div>
              )}

              {/* Expected Attendance */}
              {selectedEvent.expected_attendance && (
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <Users className="w-5 h-5 text-[#C4A052]" />
                  <div>
                    <p className="font-medium">{formatNumber(selectedEvent.expected_attendance)} Expected</p>
                    <p className="text-xs text-gray-400">Estimated attendance</p>
                  </div>
                </div>
              )}

              {/* Talking Points */}
              {selectedEvent.talking_points && selectedEvent.talking_points.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Talking Points</h4>
                  <ul className="space-y-1">
                    {selectedEvent.talking_points.map((point, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-[#C4A052]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Prep Notes */}
              {selectedEvent.prep_notes && (
                <div className="bg-[#C4A052]/10 border border-[#C4A052]/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[#C4A052] mb-2">Preparation Notes</h4>
                  <p className="text-gray-300 text-sm">{selectedEvent.prep_notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (Admin) */}
      {showCreateModal && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Event</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 text-sm">Event creation form coming soon.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEEvents;
